import React, { useEffect, useState, FC, Dispatch, SetStateAction } from 'react'
import { Button, Divider, Input, InputNumber, Modal, notification, Select as AntSelect, Space, Table, Tooltip, Typography } from 'antd'
import { Select } from '@chakra-ui/react'

import { apiUrl } from '../../globals'
import { Course } from '../Course/types'
import axios from 'axios'
import { FlexRow } from '../FlexboxReusables/FlexItems'
import LoadingSpinner from '../LoadingSpinner'
import { ExistingTestCourseAccesses } from '../Editor/SettingsRow/EditorSettingsRow'
import { CheckOutlined, CopyOutlined, DeleteOutlined } from '@ant-design/icons'

export const TestCourseAccessManagement: FC<TestCourseAccessManagementProps> = ({...props}) => { // i18n
  const [ loadingGet, setLoadingGet ] = useState(true)
  const [ loadingCreate, setLoadingCreate ] = useState(false)
  const [ copiedLink, setCopiedLink ] = useState<string | null>(null)
  const [ numberOfParticipants, setNumberOfParticipants ] = useState<number>(0)
  const [ partners, setPartners ] = useState<Array<string>>([])
  const [ selectedPartner, setSelectedPartner ] = useState<string>('')
  const [ email, setEmail ] = useState<string>('')
  const [ companyName, setCompanyName ] = useState<string>('')
  const [ address, setAddress ] = useState<string>('')
  const [ zipCode, setZipCode ] = useState<string>('')
  const [ city, setCity ] = useState<string>('')
  const [ country, setCountry ] = useState<string>('')
  const [ contactPerson, setContactPerson ] = useState<string>('')
  const [ salutationOfContactPerson, setSalutationOfContactPerson ] = useState<string>('')

  const {
    course,
    showTestCourseAccessModal,
    setShowTestCourseAccessModal,
    existingTestCourseAccesses,
    setExistingTestCourseAccesses
  } = props

  useEffect(() => {
    getTestCourseAccess()
    listPartners()
  }, [])

  const getTestCourseAccess = () => {
    axios.get<GetTestCourseAccessResponse>(`${apiUrl}/course/test-access/${course.id}`)
      .then((response) => {
        const mappedData = response.data.map((access) => ({
          linkForParticipants: access.linkForParticipants,
          paidParticipantsCount: access.paidParticipantsCount,
          examTakenCount: access.examTakenCount,
          etPartnerName: access.etPartnerName || '/'
        }))

        setExistingTestCourseAccesses(mappedData)
      })
      .catch((error) => {
        if (error.response.status === 404) {
          return // expected 404 response (no test course access exists)
        }

        console.error('Error while fetching test course accesses:', error)
      })
      .finally(() => setLoadingGet(false))
  }

  const listPartners = () => {
    axios.get(`${apiUrl}/list-partners/all`)
      .then((response) => {
        setPartners(sanitizeAndSortPartnerNames(response.data))
      })
      .catch((error) => console.error('Error while fetching partners:', error))
  }

  const sanitizeAndSortPartnerNames = (partnerNames: Array<string>) => {
    const sanitizedNames = partnerNames.map((name) => {
      const sanitized = name.substring(3, name.length).toLowerCase()

      return sanitized.charAt(0).toUpperCase() + sanitized.slice(1)
    })

    return sanitizedNames.sort()
  }

  const createTestCourseAccess = () => {
    setLoadingCreate(true)

    axios.post<TestCourseAccess>(`${apiUrl}/course/test-access/${course.id}/create`,
      {
        courseId: course.id,
        numberOfParticipants,
        etPartnerName: selectedPartner,
        email: email || undefined,
        companyName: companyName || undefined,
        address: address || undefined,
        zipCode: zipCode || undefined,
        city: city || undefined,
        country: country || undefined,
        contactPerson: contactPerson || undefined,
        salutationOfContactPerson: salutationOfContactPerson || undefined
      })
      .then((response) => {
        setExistingTestCourseAccesses([ ...existingTestCourseAccesses, response.data ])
        setNumberOfParticipants(0)
        setSelectedPartner('')
        setEmail('')
        setCompanyName('')
        setAddress('')
        setZipCode('')
        setCity('')
        setCountry('')
        setContactPerson('')
        setSalutationOfContactPerson('')

        notification.success({
          message: 'Zugang erstellt!',
          description: 'Der Zugang wurde erfolgreich erstellt.',
          placement: 'bottomRight'
        })
      })
      .catch((error) => console.error('Error while creating test course access:', error))
      .finally(() => setLoadingCreate(false))
  }

  const deleteTestCourseAccess = (linkForParticipants: string) => {
    axios.delete(`${apiUrl}/course/test-access/${course.id}/${linkForParticipants}`)
      .then(() => setExistingTestCourseAccesses(existingTestCourseAccesses.filter((access) => access.linkForParticipants !== linkForParticipants)))
      .catch((error) => console.error('Error while deleting test course access:', error))
  }

  const confirmDelete = (linkForParticipants: string) => {
    Modal.confirm({
      title: 'Zugang löschen',
      content: 'Sind Sie sicher, dass Sie diesen Zugang löschen möchten? Dies kann nicht rückgängig gemacht werden.',
      okText: 'Ja, löschen',
      okType: 'danger',
      cancelText: 'Abbrechen',
      centered: true,
      onOk: () => deleteTestCourseAccess(linkForParticipants)
    })
  }

  const deleteSelectedPartner = () => {
    setPartners(partners.filter(partner => partner !== selectedPartner))
    setSelectedPartner('')
  }

  const copyToClipboard = (text: string, linkForParticipants: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setCopiedLink(linkForParticipants)
      })
      .catch((error) => console.error('Error while copying to clipboard:', error))
  }

  const columns = [
    {
      title: 'Link',
      dataIndex: 'link',
      key: 'link',
      render: (linkForParticipants: string) => (
        <Typography.Text style={{ wordBreak: 'break-all' }}>
          {`https://cw.et360.de/exams/${linkForParticipants}`}
        </Typography.Text>
      ),
    },
    {
      title: 'Partner',
      dataIndex: 'etPartnerName',
      key: 'etPartnerName',
    },
    {
      title: 'Teilnehmeranzahl',
      dataIndex: 'paidParticipantsCount',
      key: 'paidParticipantsCount',
    },
    {
      title: 'Bisher genutzt',
      dataIndex: 'examTakenCount',
      key: 'examTakenCount',
    },
    {
      title: 'Aktionen',
      key: 'actions',
      render: (_: unknown /* only here to make TSC happy */, record: TestCourseAccess) => (
        <FlexRow justifyContent='center'>
          <Space size="middle">
            {copiedLink === record.linkForParticipants ? (
              <Tooltip title="Kopiert!">
                <CheckOutlined style={{ color: 'green', marginRight: '1.1em' }} />
              </Tooltip>
            ) : (
              <Tooltip title="In die Zwischenablage kopieren">
                <Button
                  type="text"
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(`https://cw.et360.de/exams/${record.linkForParticipants}`, record.linkForParticipants)}
                />
              </Tooltip>
            )}
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              onClick={() => confirmDelete(record.linkForParticipants)}
            />
          </Space>
        </FlexRow>
      ),
    }
  ]

  const dataSource = existingTestCourseAccesses.map((access, index) => ({
    key: index,
    link: access.linkForParticipants,
    etPartnerName: access.etPartnerName,
    paidParticipantsCount: access.paidParticipantsCount,
    examTakenCount: access.examTakenCount,
    linkForParticipants: access.linkForParticipants,
  }))

  return (
    <Modal
      title='Zugangsverwaltung'
      open={showTestCourseAccessModal}
      onCancel={() => setShowTestCourseAccessModal(false)}
      centered
      width={850}
      bodyStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
      footer={
        <FlexRow width='100%' justifyContent='space-between'>
          <Button onClick={() => setShowTestCourseAccessModal(false)}>
            Schließen
          </Button>
          <Button
            type='primary'
            onClick={() => createTestCourseAccess()}
            disabled={
              numberOfParticipants === 0 ||
              !email.trim() ||
              !companyName.trim() ||
              !address.trim() ||
              !zipCode.trim() ||
              !city.trim() ||
              !country.trim() ||
              !contactPerson.trim() ||
              !salutationOfContactPerson
            }
          >
            Zugang anlegen
          </Button>
        </FlexRow>
      }
    >
      {renderCreateNewTestCourseAccess(numberOfParticipants, setNumberOfParticipants, partners, selectedPartner, setSelectedPartner, deleteSelectedPartner, email, setEmail, companyName, setCompanyName, address, setAddress, zipCode, setZipCode, city, setCity, country, setCountry, contactPerson, setContactPerson, salutationOfContactPerson, setSalutationOfContactPerson)}
      <Divider style={{ margin: '1em 0' }} />
      {loadingGet ? (
        <FlexRow margin='2em 0' justifyContent='flex-start'>
          <LoadingSpinner loadingText="Lade Testzugänge" height='5em' />
        </FlexRow>
      ) : (
        <>
          <FlexRow margin='0.5em 0'>
            <Typography.Text strong>Bisher angelegte Zugänge</Typography.Text>
          </FlexRow>
          <Table
            columns={columns}
            dataSource={dataSource}
            pagination={{ pageSize: 5 }}
            scroll={{ y: 440 }}
            locale={{ emptyText: 'Keine Testzugänge vorhanden' }}
          />
        </>
      )}
    </Modal>
  )
}

const renderCreateNewTestCourseAccess = (
  numberOfParticipants: number,
  setNumberOfParticipants: Dispatch<SetStateAction<number>>,
  partners: Array<string>,
  selectedPartner: string,
  setSelectedPartner: Dispatch<SetStateAction<string>>,
  deleteSelectedPartner: () => void,
  email: string,
  setEmail: Dispatch<SetStateAction<string>>,
  companyName: string,
  setCompanyName: Dispatch<SetStateAction<string>>,
  address: string,
  setAddress: Dispatch<SetStateAction<string>>,
  zipCode: string,
  setZipCode: Dispatch<SetStateAction<string>>,
  city: string,
  setCity: Dispatch<SetStateAction<string>>,
  country: string,
  setCountry: Dispatch<SetStateAction<string>>,
  contactPerson: string,
  setContactPerson: Dispatch<SetStateAction<string>>,
  salutationOfContactPerson: string,
  setSalutationOfContactPerson: Dispatch<SetStateAction<string>>
) => {
  const labelStyle = { marginBottom: '0.35em', display: 'block' } as const
  const gridStyle = {
    margin: '1em 0',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '16px',
    alignItems: 'end'
  }
  return (
    <>
      <FlexRow margin='0.5em 0'>
        <Typography.Text strong>Neuen Zugang anlegen</Typography.Text>
      </FlexRow>
      <div style={gridStyle}>
        <div>
          <Typography.Text strong style={labelStyle}>Teilnehmer</Typography.Text>
          <InputNumber
            placeholder='Anzahl'
            min={0}
            max={1000}
            value={numberOfParticipants}
            onChange={(value) => setNumberOfParticipants(value as number)}
            style={{ width: '100%' }}
          />
        </div>
        <div>
          <Typography.Text strong style={labelStyle}>E-Mail</Typography.Text>
          <Input
            type='email'
            placeholder='E-Mail-Adresse'
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{ width: '100%' }}
            required
          />
        </div>
        <div>
          <Typography.Text strong style={labelStyle}>Partner</Typography.Text>
          <FlexRow justifyContent='flex-start' alignItems='center' style={{ gap: '8px' }}>
            <Select
              placeholder='Partner auswählen'
              value={selectedPartner}
              onChange={(e) => setSelectedPartner(e.target.value)}
              style={{ flex: 1, minWidth: 0 }}
            >
              {partners.map((partner) => (
                <option key={partner} value={partner}>
                  {partner}
                </option>
              ))}
            </Select>
            {selectedPartner && (
              <Tooltip title='Partner löschen'>
                <Button type='text' icon={<DeleteOutlined />} onClick={deleteSelectedPartner} danger/>
              </Tooltip>
            )}
          </FlexRow>
        </div>
        <div>
          <Typography.Text strong style={labelStyle}>Firma</Typography.Text>
          <Input
            placeholder='Firma'
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            style={{ width: '100%' }}
            required
          />
        </div>
        <div>
          <Typography.Text strong style={labelStyle}>Adresse</Typography.Text>
          <Input
            placeholder='Adresse'
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            style={{ width: '100%' }}
            required
          />
        </div>
        <div />
        <div>
          <Typography.Text strong style={labelStyle}>PLZ</Typography.Text>
          <Input
            placeholder='PLZ'
            value={zipCode}
            onChange={(e) => setZipCode(e.target.value)}
            style={{ width: '100%' }}
            required
          />
        </div>
        <div>
          <Typography.Text strong style={labelStyle}>Ort</Typography.Text>
          <Input
            placeholder='Ort'
            value={city}
            onChange={(e) => setCity(e.target.value)}
            style={{ width: '100%' }}
            required
          />
        </div>
        <div>
          <Typography.Text strong style={labelStyle}>Land</Typography.Text>
          <Input
            placeholder='Land'
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            style={{ width: '100%' }}
            required
          />
        </div>
        <div style={{ gridColumn: '1 / -1', gridRow: 4, display: 'flex', justifyContent: 'center', alignItems: 'end', gap: '24px' }}>
          <div>
            <Typography.Text strong style={labelStyle}>Anrede</Typography.Text>
            <AntSelect
              placeholder='Anrede'
              value={salutationOfContactPerson || undefined}
              onChange={(value) => setSalutationOfContactPerson(value ?? '')}
              options={[
                { value: 'Herr', label: 'Herr' },
                { value: 'Frau', label: 'Frau' }
              ]}
              style={{ width: 112 }}
            />
          </div>
          <div style={{ width: 280 }}>
            <Typography.Text strong style={labelStyle}>Unterweisungsperson</Typography.Text>
            <Input
              placeholder='Unterweisungsperson'
              value={contactPerson}
              onChange={(e) => setContactPerson(e.target.value)}
              style={{ width: '100%' }}
              required
            />
          </div>
        </div>
      </div>
    </>
  )
}


type TestCourseAccessManagementProps = {
  course: Course
  showTestCourseAccessModal: boolean
  setShowTestCourseAccessModal: Dispatch<SetStateAction<boolean>>
  existingTestCourseAccesses: ExistingTestCourseAccesses
  setExistingTestCourseAccesses: Dispatch<SetStateAction<ExistingTestCourseAccesses>>
}

type TestCourseAccess = {
  linkForParticipants: string
  examTakenCount: number
  paidParticipantsCount: number
  etPartnerName: string
}

type GetTestCourseAccessResponse = Array<TestCourseAccess>

export default TestCourseAccessManagement